import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { TrendingUp, ThumbsUp, Brain, Smile, ArrowRight } from "lucide-react";
import { ProgressRing } from "./ConsultaComponents";
import { useLanguage } from "../i18n/LanguageContext";
import { useIsWebView } from "../hooks/useIsWebView";

const MetricsSection = ({ response, t }) => (
  <div className="bg-white/5 border border-white/10 rounded-3xl p-8" data-testid="metrics-section">
    <h2 className="font-['Outfit'] text-lg font-semibold text-white/70 text-center mb-8">{t("result_analysis_title")}</h2>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-8 justify-items-center">
      <ProgressRing value={response.success_probability} label={t("result_success")} icon={TrendingUp} color="#10B981" />
      <ProgressRing value={response.recommendation_level} label={t("result_recommendation_level")} icon={ThumbsUp} color="#002FA7" />
      <ProgressRing value={response.stress_level} label={t("result_stress")} icon={Brain} color="#EF4444" />
      <ProgressRing value={response.happiness_level} label={t("result_happiness")} icon={Smile} color="#F59E0B" />
    </div>
  </div>
);

const RecommendationSection = ({ response, t }) => (
  <div className="bg-gradient-to-br from-[#002FA7]/20 to-[#5B21B6]/20 border border-[#002FA7]/30 rounded-3xl p-8" data-testid="recommendation-section">
    <div className="flex items-center gap-3 mb-4">
      <span className="text-2xl">&#10003;</span>
      <h2 className="font-['Outfit'] text-xl font-semibold text-white">{t("result_recommendation")}</h2>
    </div>
    <p className="text-xl text-white/90 leading-relaxed mb-4">{response.recommendation}</p>
    <div className="mt-4 p-4 bg-white/5 rounded-2xl border border-white/10">
      <p className="text-sm font-medium text-white/50 mb-2">{t("result_why")}</p>
      <p className="text-white/80 leading-relaxed">{response.recommendation_reason}</p>
    </div>
  </div>
);

const ListSection = ({ items, title, emoji, borderColor = "border-white/10", bgColor = "bg-white/5", dotColor = "bg-[#002FA7]", testId }) => (
  <div className={`${bgColor} border ${borderColor} rounded-3xl p-8`} data-testid={testId}>
    <div className="flex items-center gap-3 mb-6">
      <span className="text-2xl">{emoji}</span>
      <h2 className="font-['Outfit'] text-xl font-semibold text-white">{title}</h2>
    </div>
    <ul className="space-y-4">
      {items?.map((item) => (
        <li key={item} className="flex items-start gap-4">
          <div className={`w-2 h-2 rounded-full ${dotColor} mt-2.5 flex-shrink-0`} />
          <p className="text-white/80 leading-relaxed">{item}</p>
        </li>
      ))}
    </ul>
  </div>
);

const PremiumTag = ({ t }) => (
  <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 border border-amber-500/30">{t("result_premium_tag")}</span>
);

const Top3PathsSection = ({ paths, t }) => (
  <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/5 border border-amber-500/20 rounded-3xl p-8" data-testid="top3-paths-section">
    <div className="flex items-center gap-3 mb-6">
      <span className="text-2xl">&#127942;</span>
      <h2 className="font-['Outfit'] text-xl font-semibold text-white">{t("result_top3")}</h2>
      <PremiumTag t={t} />
    </div>
    <div className="space-y-4">
      {paths.map((path, idx) => (
        <div key={path.path} className="p-5 bg-white/5 border border-white/10 rounded-2xl">
          <h3 className="font-semibold text-white flex items-center gap-2 mb-2">
            <span className="text-amber-400 font-mono text-sm">{String(idx + 1).padStart(2, "0")}</span>
            {path.path}
          </h3>
          <p className="text-white/70 text-sm mb-3">{path.description}</p>
          <div className="flex gap-4">
            <div className="flex items-center gap-2">
              <div className="h-1.5 w-16 bg-white/10 rounded-full overflow-hidden"><div className="h-full bg-emerald-500 rounded-full" style={{ width: `${path.confidence}%` }} /></div>
              <span className="text-xs text-emerald-400">{path.confidence}% {t("result_confidence")}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-1.5 w-16 bg-white/10 rounded-full overflow-hidden"><div className="h-full bg-red-500 rounded-full" style={{ width: `${path.risk}%` }} /></div>
              <span className="text-xs text-red-400">{path.risk}% {t("result_risk")}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const ActionPlanSection = ({ steps, t }) => (
  <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/5 border border-amber-500/20 rounded-3xl p-8" data-testid="action-plan-section">
    <div className="flex items-center gap-3 mb-6">
      <span className="text-2xl">&#128203;</span>
      <h2 className="font-['Outfit'] text-xl font-semibold text-white">{t("result_action_plan")}</h2>
      <PremiumTag t={t} />
    </div>
    <div className="space-y-3">
      {steps.map((step, idx) => (
        <div key={step} className="flex items-start gap-4 p-4 bg-white/5 rounded-xl">
          <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
            <span className="text-sm font-bold text-amber-400">{idx + 1}</span>
          </div>
          <p className="text-white/80 leading-relaxed pt-1">{step}</p>
        </div>
      ))}
    </div>
  </div>
);

const ConsultaResult = ({ response, onReset }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const isWebView = useIsWebView();

  const hasTop3 = response.top3_paths && response.top3_paths.length > 0;
  const hasActionPlan = response.action_plan && response.action_plan.length > 0;
  const hasPremiumSummary = Boolean(response.premium_summary);
  const isNotAvanza = response.plan_tier !== "avanza";

  return (
    <div className="space-y-8 animate-fade-in">
      <MetricsSection response={response} t={t} />
      <RecommendationSection response={response} t={t} />

      <ListSection
        items={response.motives}
        title={t("result_motives")}
        emoji="&#128202;"
        testId="motives-section"
      />

      <ListSection
        items={response.risks}
        title={t("result_risks")}
        emoji="&#9888;"
        borderColor="border-[#EF4444]/20"
        bgColor="bg-[#EF4444]/5"
        dotColor="bg-[#EF4444]"
        testId="risks-section"
      />

      <div className="bg-white/5 border border-white/10 rounded-3xl p-8" data-testid="alternative-section">
        <div className="flex items-center gap-3 mb-4">
          <span className="text-2xl">&#128260;</span>
          <h2 className="font-['Outfit'] text-xl font-semibold text-white">{t("result_alternative")}</h2>
        </div>
        <p className="text-white/80 leading-relaxed">{response.alternative}</p>
      </div>

      {hasTop3 && <Top3PathsSection paths={response.top3_paths} t={t} />}
      {hasActionPlan && <ActionPlanSection steps={response.action_plan} t={t} />}

      {hasPremiumSummary && (
        <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/5 border border-amber-500/20 rounded-3xl p-8" data-testid="premium-summary-section">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-2xl">&#11088;</span>
            <h2 className="font-['Outfit'] text-xl font-semibold text-white">{t("result_premium_summary")}</h2>
            <PremiumTag t={t} />
          </div>
          <p className="text-white/80 leading-relaxed text-lg">{response.premium_summary}</p>
        </div>
      )}

      {isNotAvanza && !isWebView && (
        <div className="border border-dashed border-amber-500/30 rounded-3xl p-8 text-center" data-testid="upsell-section">
          <p className="text-white/50 mb-3">{t("result_upsell")} <span className="text-amber-400 font-semibold">Avanza</span>:</p>
          <p className="text-white/70 text-sm mb-4">{t("result_upsell_features")}</p>
          <Button onClick={() => navigate("/pricing")} className="bg-gradient-to-r from-amber-500 to-orange-500 hover:opacity-90 text-black rounded-full px-6 font-medium" data-testid="upsell-btn">
            {t("result_upsell_btn")}
          </Button>
        </div>
      )}
      {isNotAvanza && isWebView && (
        <div className="border border-dashed border-white/10 rounded-3xl p-6 text-center" data-testid="webview-sub-text-result">
          <p className="text-white/40 text-sm">{t("webview_sub_managed")}</p>
        </div>
      )}

      <div className="text-center pt-8">
        <Button onClick={onReset} className="bg-white/10 hover:bg-white/20 text-white rounded-full px-8 py-6 font-medium" data-testid="new-question-btn">
          {t("result_new_consultation")}<ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
};

export default ConsultaResult;
